import { Button, Divider, Empty, List, Modal, Row } from "antd";
import React from "react";
import { Settings, SettingsConfig } from "@version2/domain-package";
import Fields, { FieldProps } from "./Fields";
import type { ListFieldData, UserDocSelector } from "./types";

const createUniqueId = () => {
	if (
		typeof globalThis !== "undefined" &&
		typeof globalThis.crypto?.randomUUID === "function"
	) {
		return globalThis.crypto.randomUUID();
	}

	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
};

type ListFieldProps = {
	settingConfig: SettingsConfig;
	settings: Settings;
	onSettingsChange: (settings: Settings) => void;
	userDocSelector: UserDocSelector;
	customFields?: { [key: string]: (props: FieldProps) => React.ReactElement };
};

const ListItem = List.Item as unknown as React.ComponentType<
	React.PropsWithChildren<{
		actions?: React.ReactNode[];
	}>
>;

const ListField = ({
	settingConfig,
	settings,
	onSettingsChange,
	userDocSelector,
	customFields,
}: ListFieldProps) => {
	const listData = settingConfig._.data as ListFieldData | undefined;
	const settingsChildren = settingConfig._.children || [];
	const nameId = listData?.item_name_id || "id";

	const [selectedIndex, setSelectedIndex] = React.useState<number>(-1);

	const getCurrentValues = () => (settings?.value || []) as Settings[];

	const getChildDefaultValue = (id: string) =>
		settingsChildren.find((child) => child._.id === id)?._.default_value;

	const onAddItem = () => {
		const displayName = getChildDefaultValue(nameId) ?? "New item";
		const newSettings = new Settings({
			id: createUniqueId(),
			settingsConfigId: settingConfig._.id,
			editedValue: [
				{
					settingsConfigId: nameId,
					value: displayName,
				},
			],
		});
		const currentValues = getCurrentValues();
		onSettingsChange(
			settings.copyWith({
				editedValue: [...currentValues, newSettings],
			})
		);
		setSelectedIndex(currentValues.length);
	};

	const onItemValueChange = (index: number, setting: Settings[]) => {
		const newSettings = [...getCurrentValues()];
		newSettings[index] = newSettings[index].copyWith({ editedValue: setting });
		onSettingsChange(settings.copyWith({ editedValue: newSettings }));
	};

	const onRemoveItem = (index: number) => {
		const newSettings = [...getCurrentValues()];
		newSettings.splice(index, 1);
		onSettingsChange(settings.copyWith({ editedValue: newSettings }));
	};

	const onEdit = (index: number) => {
		setSelectedIndex(index);
	};

	const listValues = getCurrentValues();

	return (
		<>
			<List bordered>
				{listValues.length === 0 && (
					<div
						style={{
							alignItems: "center",
							display: "flex",
							flexDirection: "column",
							padding: "16px",
						}}
					>
						<Empty
							description={`No ${listData?.plural_name || "items"}`}
						/>
						<Button
							onClick={onAddItem}
							type="link"
							size="small"
							style={{ margin: "16px" }}
						>
							Add a new {listData?.singular_name || "item"}
						</Button>
					</div>
				)}
				{listValues.map((setting, index) => {
					const nestedValues = (setting.value || []) as Settings[];
					const fieldValue = nestedValues.find(
						(fieldSetting) => fieldSetting._.settingsConfigId === nameId
					)?.value;
					const displayName = fieldValue ?? getChildDefaultValue(nameId) ?? "New item";
					return (
						<ListItem
							key={setting._.id}
							actions={[
								<Button
									onClick={() => onEdit(index)}
									type="link"
									size="small"
									key="edit"
								>
									Edit
								</Button>,
							]}
						>
							<div style={{ fontWeight: "bold" }}>{displayName}</div>
						</ListItem>
					);
				})}
			</List>
			{listValues.length > 0 && (
				<Button
					onClick={onAddItem}
					type="primary"
					size="small"
					style={{
						margin: "16px",
						display: "block",
						marginLeft: "auto",
						marginRight: "auto",
					}}
				>
					Add a new form
				</Button>
			)}
			{selectedIndex !== -1 && (
				<Modal
					title={
						<>
							<div style={{ padding: "16px 24px" }}>
								<div>{`Edit ${listData?.singular_name || "item"}`}</div>
							</div>
							<Divider style={{ margin: "0" }} />
						</>
					}
					transitionName=""
					open={selectedIndex !== -1}
					onCancel={() => {
						setSelectedIndex(-1);
					}}
					styles={{
						body: {
							padding: "24px",
						},
					}}
					footer={
						<>
							<Divider />
							<Row
								justify="space-between"
								style={{
									padding: "0 24px 24px 24px",
								}}
							>
								<Button
									key="delete"
									danger
									onClick={() => {
										onRemoveItem(selectedIndex);
										setSelectedIndex(-1);
									}}
								>
									Delete
								</Button>
								<Row>
									<Button
										key="close"
										onClick={() => {
											setSelectedIndex(-1);
										}}
									>
										Cancel
									</Button>
									<Button
										key="submit"
										type="primary"
										onClick={() => {
											setSelectedIndex(-1);
										}}
									>
										Done
									</Button>
								</Row>
							</Row>
						</>
					}
				>
					<Fields
						settingsConfig={settingConfig._.children || []}
						settings={
							(settings?.value[selectedIndex]?.value || []) as Settings[]
						}
						onSettingsListChange={(updatedSettings: Settings[]) =>
							onItemValueChange(selectedIndex, updatedSettings)
						}
						className="horizontal-flex"
						userDocSelector={userDocSelector}
						labelCol={{ span: 6 }}
						customFields={customFields}
					/>
				</Modal>
			)}
		</>
	);
};

export default ListField;
